"use client";
import { DownOne, Sort, UpOne } from "@icon-park/svg";
import clsx from "clsx";
import {
  FC,
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  forwardRef,
} from "react";
import Icon from "../Icon";
import { ScrollArea, ScrollBar } from "../ScrollArea";

export type SortDirection = "unsorted" | "desc" | "asc" | false;

export interface TableProps {
  header?: ReactNode;
  totalRows?: number;
  border?: boolean;
  emptyMessage?: ReactNode;
  disableStateHandling?: boolean;
}

const tableCellPadding = "py-2xs px-2xs";

export type WithClassNameProps<T = any> = T & {
  className?: string;
};

export const TableHead = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(function TableHead({ className, ...props }, ref) {
  return (
    <thead
      ref={ref}
      className={clsx(
        "z-10 sticky top-none bg-neutral-none after:absolute after:bottom-none after:left-none after:right-none after:h-[1px] after:bg-neutral-50 [&_tr:first-child]:border-t-none",
        className,
      )}
      {...props}
    />
  );
});

const TableHeadCell: FC<
  PropsWithChildren<WithClassNameProps<{ colSpan?: number }>>
> = ({ className, children, colSpan }) => {
  return (
    <th
      colSpan={colSpan}
      className={clsx(
        "whitespace-nowrap text-neutral-675/60 font-normal text-left text-app-body-2 bg-neutral-0",
        className,
      )}
    >
      <div className={clsx("h-full", tableCellPadding)}>{children}</div>
    </th>
  );
};

export interface SortableTableHeadCellProps {
  onSort: MouseEventHandler;
  isSorted: boolean | string;
  direction: SortDirection;
}

const SortableTableHeadCell = ({
  children,
  onSort,
  direction,
  isSorted,
}: PropsWithChildren<SortableTableHeadCellProps>) => {
  return (
    <TableHeadCell
      className={clsx({
        "text-primary-800": isSorted,
      })}
    >
      <button
        className="flex items-center hover:text-primary-800 active:text-primary-850"
        onClick={onSort}
      >
        {children}
        <Icon
          icon={!isSorted ? Sort : direction === "asc" ? UpOne : DownOne}
          className="ms-xs"
        />
      </button>
    </TableHeadCell>
  );
};

const TableCell: FC<
  PropsWithChildren<WithClassNameProps<{ colSpan?: number }>>
> = ({ children, className, colSpan }) => {
  return (
    <td
      colSpan={colSpan}
      className={clsx(className, "text-app-body-2", tableCellPadding)}
    >
      {children}
    </td>
  );
};

const TableRow = forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(function TableRow({ className, ...props }, ref) {
  return (
    <tr
      ref={ref}
      className={clsx("group border-b border-neutral-50", className)}
      {...props}
    />
  );
});

export const TableBody = forwardRef<
  HTMLTableSectionElement,
  PropsWithChildren<React.HTMLAttributes<HTMLTableSectionElement>>
>(function TableBody({ children, ...props }, ref) {
  return (
    <tbody ref={ref} {...props} className={"[&_tr:last-child]:border-none"}>
      {children}
    </tbody>
  );
});

export const Table = forwardRef<
  HTMLTableElement,
  TableProps & React.HTMLAttributes<HTMLTableElement>
>(function Table(
  {
    children,
    totalRows,
    header,
    border,
    emptyMessage,
    disableStateHandling,
    ...props
  },
  ref,
) {
  return (
    <ScrollArea
      className={clsx(
        "flex flex-col max-h-full border-t border-neutral-50",
        {
          "border rounded-2xs": border,
        },
        props.className,
      )}
    >
      {header && (
        <div className="px-s py-3xs border-b border-neutral-50">{header}</div>
      )}
      {(disableStateHandling || !!totalRows) && (
        <table ref={ref} {...props} className="w-full">
          {children}
        </table>
      )}
      {!disableStateHandling && !totalRows && (
        <div className="flex flex-col items-center gap-2xs mx-auto my-m text-center">
          {emptyMessage}
        </div>
      )}
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  );
});

export default Object.assign(Table, {
  Cell: TableCell,
  Row: TableRow,
  HeadCell: TableHeadCell,
  SortableHeadCell: SortableTableHeadCell,
  Head: TableHead,
  Body: TableBody,
});
